import type { TradeOrder, VybeDealInternal } from "./types";
import { VybeDealType, VybeOrderType } from "./types";

export function isPositionBalanceOperation(trade: TradeOrder): boolean {
  const comment = trade.comment?.toLowerCase() ?? "";
  return (
    trade.orderType === VybeOrderType.Balance ||
    trade.orderType === VybeOrderType.Credit ||
    comment.includes("deposit") ||
    comment.includes("withdrawal")
  );
}

export function isDealBalanceOperation(deal: VybeDealInternal): boolean {
  return (
    deal.type === VybeDealType.Balance ||
    deal.type === VybeDealType.Credit ||
    deal.type === VybeDealType.Charge ||
    deal.type === VybeDealType.Correction ||
    deal.type === VybeDealType.Bonus ||
    deal.type === VybeDealType.Commission ||
    deal.type === VybeDealType.DailyCommission ||
    deal.type === VybeDealType.MonthlyCommission ||
    deal.type === VybeDealType.DailyAgentCommission ||
    deal.type === VybeDealType.MonthlyAgentCommission ||
    deal.type === VybeDealType.InterestRate
  );
}

export function netPositionPnl(position: TradeOrder): number {
  return (position.profit ?? 0) + (position.swap ?? 0) - (position.commission ?? 0);
}

export interface HistoryPositionsSummary {
  total: number;
  tradeCount: number;
  deposits: number;
  /** Amount withdrawn (positive number). */
  withdrawals: number;
  netPnl: number;
  tradingProfit: number;
  swap: number;
  commission: number;
  profitable: number;
  losing: number;
}

export interface HistoryDealsSummary {
  total: number;
  tradeCount: number;
  deposits: number;
  withdrawals: number;
  netPnl: number;
  profitable: number;
  losing: number;
}

export function computeHistoryPositionsSummary(
  positions: TradeOrder[]
): HistoryPositionsSummary {
  let deposits = 0;
  let withdrawals = 0;
  let tradingProfit = 0;
  let swap = 0;
  let commission = 0;
  let profitable = 0;
  let losing = 0;
  let tradeCount = 0;

  for (const trade of positions) {
    if (isPositionBalanceOperation(trade)) {
      if (trade.profit > 0) deposits += trade.profit;
      else if (trade.profit < 0) withdrawals += Math.abs(trade.profit);
      continue;
    }

    tradeCount++;
    if (trade.profit > 0) profitable++;
    else if (trade.profit < 0) losing++;

    tradingProfit += trade.profit ?? 0;
    swap += trade.swap ?? 0;
    commission += trade.commission ?? 0;
  }

  return {
    total: positions.length,
    tradeCount,
    deposits,
    withdrawals,
    netPnl: tradingProfit + swap - commission,
    tradingProfit,
    swap,
    commission,
    profitable,
    losing,
  };
}

export function computeHistoryDealsSummary(
  deals: VybeDealInternal[]
): HistoryDealsSummary {
  let deposits = 0;
  let withdrawals = 0;
  let netPnl = 0;
  let profitable = 0;
  let losing = 0;
  let tradeCount = 0;

  for (const deal of deals) {
    if (isDealBalanceOperation(deal)) {
      if (deal.profit > 0) deposits += deal.profit;
      else if (deal.profit < 0) withdrawals += Math.abs(deal.profit);
      continue;
    }

    tradeCount++;
    const net = (deal.profit ?? 0) + (deal.swap ?? 0) - (deal.commission ?? 0);
    netPnl += net;
    if (deal.profit > 0) profitable++;
    else if (deal.profit < 0) losing++;
  }

  return {
    total: deals.length,
    tradeCount,
    deposits,
    withdrawals,
    netPnl,
    profitable,
    losing,
  };
}
